import React, { useRef, useState } from "react";
import Typography from "../../../components/Typography";
import { PDF, UPLOAD_ICON } from "../../../assets";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import Api from "../../../config";
import { getFormDataHeader } from "../../../utils";
import toast from "react-hot-toast";

type CaseDocType = {
  id: number;
  title: string;
  document_type: string;
  file: File | null;
};

function FileAProcess() {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [caseDocument, setCaseDocument] = useState<CaseDocType>({
    id: 1,
    title: "",
    document_type: "",
    file: null,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseFolderId = searchParams.get("id") || "";

  const handleCaseInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: "title" | "document_type",
  ) => {
    const { value } = e.target;
    setCaseDocument((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setCaseDocument((prev) => ({ ...prev, file: f || null }));
  };

  const handleSubmit = async () => {
    if (!caseFolderId) {
      toast.error("Missing case folder id");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    if (caseDocument.file) {
      formData.append("file_path", caseDocument.file);
    }
    formData.append("title", caseDocument.title);
    formData.append("type", caseDocument.document_type.toUpperCase());
    try {
      const response = await Api.post(
        `/lawyer/case-folders/${caseFolderId}/add-document`,
        formData,
        getFormDataHeader(),
      );
      toast.success(response?.data?.message || "Document added");
      navigate("/lawyer/my-files");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to add document");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-[#F8F8F9E5]/90  w-full py-2 px-8">
        <Typography textStyle="text-base">File A Process</Typography>
      </div>
      <div className="pt-4 pb-16 px-8 flex justify-center">
        <div className="w-full max-w-[575px]">
          <button
            type="button"
            onClick={() => navigate("/lawyer/my-files")}
            className="flex items-center gap-x-2 text-sm font-semibold text-[#272755] hover:opacity-80"
          >
            <FaArrowLeft size={14} />
            <span>Back to My Files</span>
          </button>

          <div className="mt-8">
            <div className="mt-2 flex-col flex gap-y-2">
              <span className="text-[16px] font-semibold">Title</span>
              <input
                type="text"
                name="title"
                className="w-full border border-[#CACACA] rounded outline-none bg-transparent placeholder:text-sm placeholder:text-gray-400 py-4 px-3"
                placeholder="Enter case title"
                onChange={(e) => handleCaseInputChange(e, "title")}
                value={caseDocument.title}
              />
            </div>

            <div className="mt-6 flex-col flex gap-y-2">
              <span className="text-[16px] font-semibold">
                Document Type <span className="text-red-500">*</span>
              </span>
              <div className="w-full">
                <input
                  type="text"
                  name="document_type"
                  className="w-full border border-[#CACACA] placeholder:text-gray-400 rounded outline-none bg-transparent placeholder:text-sm  py-4 px-3"
                  placeholder="Enter document type"
                  onChange={(e) => handleCaseInputChange(e, "document_type")}
                  value={caseDocument.document_type}
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-y-2 items-end justify-end">
              <span className="text-[16px] font-semibold flex w-full items-start">
                Upload documents
              </span>
              {file ? (
                <div className="w-full h-full border border-[#C7C7CC] p-6 rounded-lg">
                  <div className="flex items-start gap-x-3">
                    <img src={PDF} className="w-[28px] h-[28px]" alt="" />
                    <div className="w-full">
                      <div className="flex items-start justify-between w-full">
                        <div className="flex items-start gap-x-3">
                          <div className="flex flex-col gap-y-1">
                            <span className="text-sm font-medium text-[#232A35]">
                              {file.name}
                            </span>
                            <span className="text-[#667085] font-normal text-sm">
                              PDF (max 25 MB)
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center h-[16px] w-[16px] justify-center rounded-full bg-[#272755] ">
                          <FaCheck size={10} color="white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full h-[126px] cursor-pointer mt-3 border border-[#EAECF0] flex items-center justify-center flex-col rounded-md"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <img src={UPLOAD_ICON} className="w-[40px] h-[40px]" alt="" />
                  <span className="text-[14px] font-semibold">
                    Click to upload{" "}
                    <span className="text-[#667085]">Example receipt.pdf</span>
                  </span>
                  <span className="text-[#667085] text-sm">PDF (max 25mb)</span>
                </div>
              )}
              <input
                type="file"
                accept=".pdf"
                className="file_upload sr-only"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </div>

            <button
              type="button"
              className={`w-full ${
                loading ? "bg-opacity-50 cursor-wait" : "bg-opacity-100"
              } bg-[#272755] text-[17.8px] rounded py-4 mt-10 text-white font-semibold`}
              onClick={handleSubmit}
            >
              {loading ? "please wait..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default FileAProcess;
